import { useEffect } from "react";

import { ESCAPE, topEscapePriority } from "../components/escapeStack";
import { useUi } from "../state/ui";
import { type History, history as mapHistory, mapSurfaceVisible } from "./history";
import { useOrientation } from "./manoeuvre/useOrientation";

/** Raccourcis d'Annuler / Rétablir de la carte (K14, docs/14 §2.3 et §2.8) : ⌘Z / Ctrl+Z annule ; ⇧⌘Z,
 *  Ctrl+⇧Z ou Ctrl+Y rétablit. La touche se lit sur `e.key` (« z »), comme les autres raccourcis de l'application
 *  (App.tsx) ; `e.code` (« KeyZ ») ne sert que si `e.key` n'est pas une lettre latine (disposition cyrillique,
 *  grecque…) : tester `e.code` seul casserait les claviers AZERTY, dont la touche « Z » envoie `KeyW`. Ignoré
 *  pendant une saisie (l'annulation native du champ reste), un tracé, ou quand la carte n'est pas la surface
 *  visible (vue plein cadre, accueil). */

export type HistoryKeyAction = "undo" | "redo";

type KeyLike = Pick<KeyboardEvent, "key" | "code" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">;

const LATIN_LETTER = /^\p{Script=Latin}$/u;

const ANY_LETTER = /^\p{L}$/u;

/** La touche est la lettre `letter` : par `e.key`, ou par `e.code` seulement si `e.key` est une lettre d'une
 *  autre écriture (cyrillique, grec…). Une autre lettre latine (AZERTY : « w » sur KeyZ), une ponctuation
 *  (Dvorak : « ; » sur KeyZ) ou une touche morte (BÉPO : « Dead » sur KeyY) ne comptent pas. */
function isLetter(e: KeyLike, letter: "z" | "y"): boolean {
  const key = (e.key ?? "").toLowerCase();
  if (key === letter) return true;
  const otherScript = ANY_LETTER.test(key) && !LATIN_LETTER.test(key);
  return otherScript && e.code === (letter === "z" ? "KeyZ" : "KeyY");
}

/** Action d'historique d'un appui clavier, ou `null`. */
export function historyKeyAction(e: KeyLike): HistoryKeyAction | null {
  if (!(e.metaKey || e.ctrlKey) || e.altKey) return null; // AltGr (Ctrl+Alt) compose un caractère
  if (isLetter(e, "z")) return e.shiftKey ? "redo" : "undo";
  if (e.ctrlKey && !e.metaKey && !e.shiftKey && isLetter(e, "y")) return "redo";
  return null;
}

/** Élément de saisie (champ, zone de texte, liste, texte modifiable). */
export function isEditableTarget(target: unknown): boolean {
  const el = target as { tagName?: string; isContentEditable?: boolean } | null;
  if (!el || typeof el !== "object") return false;
  return Boolean(el.isContentEditable) || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName ?? "");
}

/** Traite un appui : vrai s'il a annulé ou rétabli (l'appui est alors consommé). `blocked` : tracé en cours.
 *  Ignoré aussi quand une fenêtre de confirmation est ouverte au-dessus de la carte (inscrite dans la pile
 *  d'Échap au rang modal) : ⌘Z ne défait rien derrière elle. `orienting` (mode « Orienter la manœuvre », docs/14
 *  §2.14) : ⌘Z vaut Échap (`cancelOrientation`), rien n'est défait dans l'historique. */
export function handleHistoryKey(
  e: KeyLike & Pick<KeyboardEvent, "target" | "defaultPrevented" | "preventDefault">,
  engine: Pick<History, "undo" | "redo" | "isActive">,
  blocked = false,
  orienting: { open: boolean; cancel: () => void } | null = null,
): boolean {
  const action = historyKeyAction(e);
  if (!action || e.defaultPrevented || blocked || !engine.isActive()) return false;
  if (topEscapePriority() >= ESCAPE.modal) return false;
  const focused = typeof document !== "undefined" ? document.activeElement : null;
  if (isEditableTarget(e.target) || isEditableTarget(focused)) return false;
  e.preventDefault();
  if (orienting?.open) {
    if (action === "undo") orienting.cancel();
    return true;
  }
  if (action === "undo") engine.undo();
  else engine.redo();
  return true;
}

/** Branche les raccourcis sur la fenêtre et tient l'historique actif seulement quand la carte est visible. */
export function useHistoryKeys(engine: History = mapHistory): void {
  const welcome = useUi((s) => s.welcome);
  const view = useUi((s) => s.view);
  const visible = mapSurfaceVisible({ welcome, view });

  useEffect(() => engine.setActive(visible), [engine, visible]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const orientation = useOrientation.getState();
      // Écriture du sens en cours : ⌘Z est consommé sans rien annuler (la réponse ferme le mode).
      const orienting = { open: orientation.open, cancel: () => (orientation.pending ? undefined : orientation.cancel()) };
      handleHistoryKey(e, engine, useUi.getState().draft !== null, orienting);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [engine]);
}
